import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { Box, Chip, Drawer, Typography, IconButton, TextField, CircularProgress, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MicIcon from '@mui/icons-material/Mic';
import MicOffIcon from '@mui/icons-material/MicOff';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import AgentService from '../services/AgentService';
import AuthService from '../services/AuthService';
import ActionConfirmationCard from './agent/ActionConfirmationCard';
import InsightsTab from './agent/InsightsTab';

const THINKING_MESSAGES = [
  'Thinking...', 'Pondering...', 'Cogitating...', 'Ruminating...',
  'Noodling on it...', 'Consulting the oracle...', 'Summoning wisdom...',
  'Connecting the dots...', 'Having a brainwave...', 'Bombinating...',
  'Defragmenting neurons...', 'Asking the universe...', 'Calculating...',
  'Deep in thought...', 'Wrangling ideas...', 'Brain goes brrr...',
  'Philosophizing...', 'Contemplating life...', 'Processing at ludicrous speed...',
  'Staring into the void (productively)...', 'Channeling my inner genius...',
];

const WORKING_MESSAGES = [
  'Working on it...', 'Making it happen...', 'Doing the thing...',
  'Executing the plan...', 'Crunching data...', 'Wrangling bytes...',
  'Doing wizard things...', 'Shuffling databases...', 'Moving bits around...',
  'Talking to the server...', 'Making magic happen...', 'Turning coffee into code...',
  'Persisting to the database...', 'One moment of excellence coming up...',
  'Almost there... probably...', 'Saving the world, one record at a time...',
];

const MORNING_GREETINGS = [
  (name) => `Good morning, ${name}! ☀️ Ready to conquer the day? What can I help you with?`,
  (name) => `Good morning, ${name}! Coffee in hand? Great, let's get things done! ☕`,
  (name) => `Rise and shine, ${name}! 🌅 The CRM awaits. What's on the agenda?`,
  (name) => `Good morning, ${name}! A fresh day, a fresh start. How can I help?`,
  (name) => `Morning, ${name}! 🐦 The early bird gets the invoice paid. What do you need?`,
];

const AFTERNOON_GREETINGS = [
  (name) => `Good afternoon, ${name}! 🌤️ Hope the morning was kind to you. What's next?`,
  (name) => `Hey ${name}! Afternoon energy kicking in? Let's make it count! 💪`,
  (name) => `Good afternoon, ${name}! Still plenty of day left — how can I help?`,
  (name) => `Hi ${name}! Post-lunch productivity mode activated. 🚀 What do you need?`,
  (name) => `Afternoon, ${name}! ☀️ Let's check something off your list. What's up?`,
];

const EVENING_GREETINGS = [
  (name) => `Good evening, ${name}! 🌙 Burning the midnight oil? I'm here to help.`,
  (name) => `Evening, ${name}! Dedicated as always. What can I do for you tonight?`,
  (name) => `Hey ${name}! 🌆 Wrapping up the day? Let me help you finish strong.`,
  (name) => `Good evening, ${name}! The stars are out and so are you. What do you need?`,
  (name) => `Evening, ${name}! 🦉 Night owls get things done. How can I assist?`,
];

function getGreeting(firstName) {
  const name = firstName || 'there';
  const h = new Date().getHours();
  const pool = h < 12 ? MORNING_GREETINGS : h < 18 ? AFTERNOON_GREETINGS : EVENING_GREETINGS;
  return pool[Math.floor(Math.random() * pool.length)](name);
}

function greetingMessage(firstName) {
  return { role: 'assistant', content: getGreeting(firstName), pendingAction: null, toolUseId: null, confirmed: null };
}


export default function AgentDrawer({ open, onClose, onLoadingChange, onResponseReceived, defaultTab = 'chat' }) {
  const { showToast } = useToast();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(defaultTab || 'chat');
  const [messages, setMessages] = useState([]);
  // Each message: { role, content, pendingAction, toolUseId, confirmed }
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const setLoadingOn  = () => { setLoading(true);  onLoadingChange?.(true); };
  const setLoadingOff = (success = false) => { setLoading(false); onLoadingChange?.(false); if (success) onResponseReceived?.(); };
  const [confirmingIdx, setConfirmingIdx] = useState(null);
  const [firstName, setFirstName] = useState('');
  const [loadingText, setLoadingText] = useState('');
  const [isListening, setIsListening] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState('');
  const messagesEndRef = useRef(null);
  const recognitionRef = useRef(null);
  const typewriterRef = useRef(null);
  const speechSupported = typeof window !== 'undefined' &&
    ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  // Fetch user's first name once on mount
  useEffect(() => {
    AuthService.account().then(data => {
      setFirstName(data?.firstName || '');
    }).catch(() => {});
  }, []);

  // Cycle through fun loading messages while waiting
  useEffect(() => {
    if (!loading) { setLoadingText(''); return; }
    const pool = confirmingIdx !== null ? WORKING_MESSAGES : THINKING_MESSAGES;
    const pick = () => pool[Math.floor(Math.random() * pool.length)];
    setLoadingText(pick());
    const id = setInterval(() => setLoadingText(pick()), 2200);
    return () => clearInterval(id);
  }, [loading, confirmingIdx]);

  // Inject greeting when drawer opens on an empty conversation; also set active tab
  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab || 'chat');
      setMessages(prev => prev.length === 0 ? [greetingMessage(firstName)] : prev);
    }
  }, [open, defaultTab, firstName]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Clean up typewriter on unmount
  useEffect(() => () => { if (typewriterRef.current) clearInterval(typewriterRef.current); }, []);

  const runTypewriter = (fullText) => {
    if (typewriterRef.current) clearInterval(typewriterRef.current);
    if (!fullText) return;
    let i = 0;
    typewriterRef.current = setInterval(() => {
      i = Math.min(i + 8, fullText.length);
      const done = i >= fullText.length;
      setMessages(prev => {
        const updated = [...prev];
        const last = updated[updated.length - 1];
        if (last?.streaming) updated[updated.length - 1] = { ...last, content: fullText.slice(0, i), streaming: !done };
        return updated;
      });
      if (done) clearInterval(typewriterRef.current);
    }, 16);
  };

  const appendStreamingMessage = () => {
    const placeholder = { role: 'assistant', content: '', pendingAction: null, toolUseId: null, confirmed: null, streaming: true };
    setMessages(prev => [...prev, placeholder]);
  };

  const handleNavigate = (nav) => {
    const { page, id, tab, filters } = nav;
    const state = { agentFilters: filters || {}, agentTab: tab || null };
    const paths = {
      home: '/home', customers: '/customers', customer: `/customers/${id}`,
      projects: '/projects', project: `/project-info/${id}`,
      invoices: '/invoices', invoice: `/invoices/${id}`,
      reports: '/reports', report: `/reports/${id}`,
      quotes: '/quotes', quote: `/quote-info/${id}`,
      contracts: '/contracts', contract: `/contracts/${id}`,
      services: '/services', service: `/services/${id}`,
      inventory: '/inventory', 'inventory-item': `/inventory/${id}`,
      employees: '/employees', employee: `/employees/${id}`,
      planner: '/planner', 'time-tracking': '/time-tracking',
      absences: '/absences', users: '/users', 'public-holidays': '/public-holidays',
    };
    const path = paths[page];
    if (path) navigate(path, { state });
  };

  const sendMessage = (text) => {
    if (!text.trim() || loading) return;
    const userMsg = { role: 'user', content: text, pendingAction: null, toolUseId: null, confirmed: null };
    const newMessages = [...messages, userMsg];
    setMessages(newMessages);
    setInputValue('');
    setLoadingOn();
    appendStreamingMessage();

    AgentService.streamChat(
      newMessages,
      (text) => { setLoadingOff(true); runTypewriter(text); },
      () => {},
      (action) => {
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.streaming) updated[updated.length - 1] = { ...last, streaming: false, pendingAction: action, toolUseId: crypto.randomUUID() };
          return updated;
        });
        setLoadingOff(true);
      },
      handleNavigate,
      () => {
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.streaming && !last.content) updated[updated.length - 1] = { ...last, streaming: false, content: "I'm not sure I understood that. Could you try rephrasing?" };
          return updated;
        });
        setLoadingOff(true);
      },
      () => {
        showToast('Failed to get response', 'error');
        setMessages(prev => {
          const updated = [...prev];
          if (updated[updated.length - 1]?.streaming) updated.pop();
          return updated;
        });
        setLoadingOff(false);
      }
    );
  };

  const streamFollowUp = (updatedMessages) => {
    setLoadingOn();
    appendStreamingMessage();
    AgentService.streamChat(
      updatedMessages,
      (text) => { setLoadingOff(true); runTypewriter(text); },
      () => {},
      (action) => {
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.streaming) updated[updated.length - 1] = { ...last, streaming: false, pendingAction: action, toolUseId: crypto.randomUUID() };
          return updated;
        });
        setLoadingOff(true);
      },
      handleNavigate,
      () => {
        setMessages(prev => {
          const updated = [...prev];
          const last = updated[updated.length - 1];
          if (last?.streaming && !last.content) updated[updated.length - 1] = { ...last, streaming: false, content: "I'm not sure I understood that. Could you try rephrasing?" };
          return updated;
        });
        setLoadingOff(true);
      },
      () => {
        setMessages(prev => {
          const updated = [...prev];
          if (updated[updated.length - 1]?.streaming) updated.pop();
          return updated;
        });
        setLoadingOff(false);
      }
    );
  };

  const handleConfirm = async (idx) => {
    const msg = messages[idx];
    setConfirmingIdx(idx);
    try {
      await AgentService.execute(msg.pendingAction.actionType, msg.pendingAction.parameters);
      showToast('Done!', 'success');
      const confirmedMessages = messages.map((m, i) => i === idx ? { ...m, confirmed: true } : m);
      const followupMsg = {
        role: 'user',
        content: `[Confirmed: ${msg.pendingAction.actionType}]`,
        pendingAction: null,
        toolUseId: msg.toolUseId,
        confirmed: true,
      };
      const updatedMessages = [...confirmedMessages, followupMsg];
      setMessages(updatedMessages);
      streamFollowUp(updatedMessages);
    } catch (e) {
      showToast('Action failed', 'error');
    } finally {
      setConfirmingIdx(null);
    }
  };

  const handleCancel = async (idx) => {
    const msg = messages[idx];
    const cancelledMessages = messages.map((m, i) => i === idx ? { ...m, confirmed: false } : m);
    const cancelMsg = {
      role: 'user',
      content: `[Cancelled: ${msg.pendingAction.actionType}]`,
      pendingAction: null,
      toolUseId: msg.toolUseId,
      confirmed: false,
    };
    const updatedMessages = [...cancelledMessages, cancelMsg];
    setMessages(updatedMessages);
    streamFollowUp(updatedMessages);
  };


  const getSpeechLang = () => {
    const lang = navigator.language || 'en';
    if (lang.startsWith('de')) return 'de-DE';
    if (lang.startsWith('fr')) return 'fr-FR';
    if (lang.startsWith('it')) return 'it-IT';
    return 'en-US';
  };

  const startListening = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = getSpeechLang();
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => setIsListening(true);

    let finalAccumulated = '';

    recognition.onresult = (event) => {
      let interim = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const t = event.results[i][0].transcript;
        if (event.results[i].isFinal) finalAccumulated += (finalAccumulated ? ' ' : '') + t.trim();
        else interim += t;
      }
      setInterimTranscript(interim);
    };

    recognition.onerror = (event) => {
      if (event.error !== 'no-speech') showToast('Microphone error: ' + event.error, 'error');
      setIsListening(false);
      setInterimTranscript('');
    };

    recognition.onend = () => {
      setIsListening(false);
      setInterimTranscript('');
      if (finalAccumulated.trim()) {
        setInputValue(finalAccumulated.trim());
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const stopListening = () => {
    recognitionRef.current?.stop();
    setIsListening(false);
    setInterimTranscript('');
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100vw', sm: 640 },
          // Frosted floating panel — the app behind blurs through it
          backgroundColor: 'rgba(255,255,255,0.86)',
          backdropFilter: 'blur(22px) saturate(140%)',
          WebkitBackdropFilter: 'blur(22px) saturate(140%)',
          borderLeft: '1px solid rgba(255,255,255,0.6)',
          borderTopLeftRadius: 22,
          borderBottomLeftRadius: 22,
          boxShadow: '-8px 0 40px -8px rgba(15,23,42,0.22)',
          // Soft brand glow in the top corner so the assistant reads as "special"
          backgroundImage: 'radial-gradient(120% 60% at 100% 0%, rgba(30,58,82,0.06) 0%, rgba(30,58,82,0) 55%)',
          display: 'flex',
          flexDirection: 'column',
        }
      }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(15,23,42,0.10)' } } }}
    >
      {/* Sticky header */}
      <Box sx={{
        px: 3,
        py: 2,
        borderBottom: '1px solid #EEF2F6',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexShrink: 0,
        gap: 2,
      }}>
        {/* Left: icon + tab switcher */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <AutoAwesomeIcon sx={{ fontSize: 18, color: '#427898', flexShrink: 0 }} />
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Chip
              label="Chat"
              size="small"
              onClick={() => setActiveTab('chat')}
              variant={activeTab === 'chat' ? 'filled' : 'outlined'}
              sx={{
                height: 26,
                fontSize: 12,
                fontWeight: activeTab === 'chat' ? 700 : 500,
                cursor: 'pointer',
                bgcolor: activeTab === 'chat' ? '#427898' : 'transparent',
                color: activeTab === 'chat' ? '#fff' : '#64748B',
                borderColor: activeTab === 'chat' ? '#427898' : '#E2E8F0',
                '&:hover': {
                  bgcolor: activeTab === 'chat' ? '#355f79' : '#F1F5F9',
                },
              }}
            />
            <Chip
              label="Insights"
              size="small"
              onClick={() => setActiveTab('insights')}
              variant={activeTab === 'insights' ? 'filled' : 'outlined'}
              sx={{
                height: 26,
                fontSize: 12,
                fontWeight: activeTab === 'insights' ? 700 : 500,
                cursor: 'pointer',
                bgcolor: activeTab === 'insights' ? '#427898' : 'transparent',
                color: activeTab === 'insights' ? '#fff' : '#64748B',
                borderColor: activeTab === 'insights' ? '#427898' : '#E2E8F0',
                '&:hover': {
                  bgcolor: activeTab === 'insights' ? '#355f79' : '#F1F5F9',
                },
              }}
            />
          </Box>
        </Box>

        {/* Right: action buttons */}
        <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
          {activeTab === 'chat' && messages.length > 0 && (
            <Tooltip title="Clear conversation">
              <IconButton
                size="small"
                onClick={() => {
                  if (typewriterRef.current) clearInterval(typewriterRef.current);
                  setMessages([greetingMessage(firstName)]);
                }}
                sx={{ color: '#94A3B8' }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={onClose} sx={{ color: '#94A3B8' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Body — switches between Chat and Insights */}
      {activeTab === 'chat' ? (
        /* Scrollable chat body */
        <Box sx={{ flex: 1, overflowY: 'auto', p: 3, display: 'flex', flexDirection: 'column' }}>
          {/* Message list */}
          {messages.map((msg, idx) => (
            <Box
              key={idx}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start',
                mb: 1.5,
              }}
            >
              {msg.role === 'user' ? (
                <Box sx={{
                  maxWidth: '80%',
                  bgcolor: '#EFF6FF',
                  border: '1px solid #BFDBFE',
                  borderRadius: 2,
                  px: 2,
                  py: 1.25,
                }}>
                  <Typography sx={{ fontSize: 13, color: '#1E293B', whiteSpace: 'pre-wrap' }}>
                    {msg.content}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ maxWidth: '90%' }}>
                  {msg.content && (
                    <Box sx={{
                      bgcolor: '#F8FAFC',
                      border: '1px solid #EEF2F6',
                      borderRadius: 2,
                      px: 2,
                      py: 1.25,
                      mb: msg.pendingAction ? 0.5 : 0,
                    }}>
                      <Box sx={{ fontSize: 13, color: '#374151', lineHeight: 1.6, '& p': { margin: '0 0 8px 0', '&:last-child': { marginBottom: 0 } }, '& ul, & ol': { margin: '0 0 8px 0', paddingLeft: 20 }, '& li': { marginBottom: 2 }, '& strong': { fontWeight: 600 }, '& code': { fontFamily: 'monospace', bgcolor: '#F1F5F9', px: 0.5, borderRadius: 0.5 } }}>
                        <ReactMarkdown>{msg.content}</ReactMarkdown>
                      </Box>
                    </Box>
                  )}
                  {msg.pendingAction && msg.confirmed === null && (
                    (() => {
                      const lastPendingIdx = messages.reduce((last, m, i) =>
                        m.role === 'assistant' && m.pendingAction && m.confirmed === null ? i : last, -1);
                      const isActive = idx === lastPendingIdx;
                      return (
                        <ActionConfirmationCard
                          pendingAction={msg.pendingAction}
                          onConfirm={() => handleConfirm(idx)}
                          onCancel={() => handleCancel(idx)}
                          loading={confirmingIdx === idx}
                          disabled={!isActive}
                        />
                      );
                    })()
                  )}
                </Box>
              )}
            </Box>
          ))}

          {/* Fun loading indicator */}
          {loading && loadingText && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <CircularProgress size={16} sx={{ color: '#427898' }} />
              <Typography sx={{ fontSize: 12, color: '#94A3B8', fontStyle: 'italic' }}>
                {loadingText}
              </Typography>
            </Box>
          )}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </Box>
      ) : (
        /* Insights tab */
        <InsightsTab
          onAskAI={(insight) => {
            setActiveTab('chat');
            const q = `Tell me more about: ${insight.title}. ${insight.description}`;
            setInputValue(q);
          }}
          onClose={onClose}
        />
      )}

      {/* Sticky footer — only for chat tab */}
      {activeTab === 'chat' && (
        <Box sx={{ px: 3, py: 2, borderTop: '1px solid #EEF2F6', flexShrink: 0 }}>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
            {/* Voice input button */}
            {speechSupported && (
              <Tooltip title={isListening ? 'Stop recording' : 'Speak your message'}>
                <IconButton
                  size="small"
                  onClick={isListening ? stopListening : startListening}
                  disabled={loading}
                  sx={{
                    flexShrink: 0,
                    color: isListening ? '#EF4444' : '#94A3B8',
                    '&:hover': { color: isListening ? '#DC2626' : '#427898' },
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.4 },
                    },
                    animation: isListening ? 'pulse 1.2s ease-in-out infinite' : 'none',
                  }}
                >
                  {isListening ? <MicOffIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                </IconButton>
              </Tooltip>
            )}

            {/* Text input */}
            <Box sx={{ flex: 1, position: 'relative' }}>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                size="small"
                placeholder={isListening ? 'Listening…' : 'Ask anything or describe what to create...'}
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage(inputValue);
                  }
                }}
                disabled={loading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontSize: 13,
                    borderRadius: '14px',
                    backgroundColor: 'rgba(255,255,255,0.7)',
                    backdropFilter: 'blur(8px)',
                    transition: 'background-color 0.2s',
                    '& fieldset': { borderColor: isListening ? '#EF4444' : 'rgba(226,232,240,0.9)', transition: 'border-color 0.2s' },
                    '&:hover fieldset': { borderColor: isListening ? '#EF4444' : '#427898' },
                    '&.Mui-focused fieldset': { borderColor: isListening ? '#EF4444' : '#427898' },
                  }
                }}
              />
              {/* Interim transcript shown below the input */}
              {isListening && interimTranscript && (
                <Typography sx={{
                  fontSize: 11,
                  color: '#94A3B8',
                  fontStyle: 'italic',
                  px: 1,
                  pt: 0.5,
                  position: 'absolute',
                  bottom: -18,
                  left: 0,
                }}>
                  {interimTranscript}
                </Typography>
              )}
            </Box>

            {/* Send button */}
            <IconButton
              size="small"
              onClick={() => sendMessage(inputValue)}
              disabled={!inputValue.trim() || loading}
              sx={{
                bgcolor: '#427898',
                color: '#fff',
                borderRadius: 1.5,
                p: 1,
                flexShrink: 0,
                '&:hover': { bgcolor: '#355f79' },
                '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
              }}
            >
              <SendIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
